import { getConsent } from "@/components/CookieBanner";

// posthog is dynamically imported only when consent is granted —
// keeps the 188 KB posthog-js bundle out of the initial page load.
type PostHogModule = typeof import("posthog-js")["default"];
let posthog: PostHogModule | null = null;

/**
 * Phase C T-C22 — PostHog analytics wired to PECR cookie consent.
 *
 * Init flow:
 *   1. On app boot, check getConsent() (from cookie banner).
 *   2. If consent.analytics === true, init PostHog with PostHog EU host
 *      (GDPR-UK compliant; data stays in EU).
 *   3. If consent.analytics === false (default), DON'T init. No tracking.
 *   4. Listen for 'tm-consent-changed' — re-init or shutdown on toggle.
 *
 * No PII in event properties — only the supabase uuid + numeric/jsonb payloads.
 */

const PUBLIC_KEY = import.meta.env.VITE_POSTHOG_PUBLIC_KEY ?? "";
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com";

let initialised = false;

async function maybeInit() {
  const consent = getConsent();
  if (!consent?.analytics) return;
  if (initialised) return;
  if (!PUBLIC_KEY) return;
  // Dynamic import keeps the 188 KB posthog-js bundle out of the initial
  // page-load chunk. Only fetched when consent has been granted.
  const mod = await import("posthog-js");
  posthog = mod.default;
  posthog.init(PUBLIC_KEY, {
    api_host: HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    autocapture: false,
  });
  initialised = true;
}

function maybeShutdown() {
  if (!initialised || !posthog) return;
  posthog.opt_out_capturing();
  posthog.reset();
  initialised = false;
}

/** Call once on app mount. */
export function bootAnalytics() {
  maybeInit();
  window.addEventListener("tm-consent-changed", (e: Event) => {
    const detail = (e as CustomEvent).detail as { analytics: boolean };
    if (detail.analytics) maybeInit();
    else maybeShutdown();
  });
}

/** Identify the current user. Call after the Clerk-Supabase bridge resolves. */
export function identifyUser(supabaseUuid: string, traits?: Record<string, unknown>) {
  if (!initialised || !posthog) return;
  posthog.identify(supabaseUuid, traits ?? {});
}

/** Fire a named event. No PII in props. */
export function trackEvent(name: string, props?: Record<string, unknown>) {
  if (!initialised || !posthog) return;
  posthog.capture(name, props ?? {});
}

/** Reset on sign-out. */
export function resetAnalytics() {
  if (!initialised || !posthog) return;
  posthog.reset();
}
